import { useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLadder } from '../contexts/LadderContext';
import { Plus, Pencil, Trash2, Users, Search, ArrowUpDown, ChevronUp, ChevronDown, RotateCcw, Archive } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper for table header class based on language
const getHeaderClass = (language) => language === 'el' 
  ? 'text-xs font-semibold tracking-wider text-muted-foreground whitespace-nowrap'
  : 'text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap';

// Calculate certainty label from sigma
const getCertaintyFromSigma = (sigma) => {
  if (sigma == null) return null;
  if (sigma >= 2.5 && sigma <= 3.0) return 'certaintyVeryHigh';
  if (sigma >= 3.1 && sigma <= 4.0) return 'certaintyHigh';
  if (sigma >= 4.1 && sigma <= 5.8) return 'certaintyMedium';
  if (sigma >= 5.9 && sigma <= 7.0) return 'certaintyLow';
  if (sigma >= 7.1) return 'certaintyVeryLow';
  return null;
};

// Calculate level from mu (μ/5) - always 2 decimals
const getLevelFromMu = (mu) => {
  if (mu == null) return null;
  return (mu / 5).toFixed(2);
};

// Format number with exactly 2 decimals
const formatNumber = (value) => {
  if (value == null) return null;
  return Number(value).toFixed(2);
};

// Get certainty sort order (lower sigma = higher certainty = sort first)
const getCertaintySortValue = (sigma) => {
  if (sigma == null) return 999;
  return sigma;
};

// Em dash for empty values
const EMPTY = '—';

export default function AdminPlayers() {
  const { t, language } = useLanguage();
  const { ladder, players, leaderboard, addPlayer, updatePlayer, deletePlayer, refreshPlayers } = useLadder();
  
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showArchived, setShowArchived] = useState(false);
  const [archivedPlayers, setArchivedPlayers] = useState([]);
  const [form, setForm] = useState({ 
    name: '', 
    display_name: '',
    email: '', 
    phone: '',
    gender: '',
    mu: '',
    sigma: '',
    titles: '',
    runner_ups: ''
  });

  // Build rank lookup from leaderboard
  const rankLookup = useMemo(() => {
    const lookup = {};
    if (leaderboard?.qualified) {
      leaderboard.qualified.forEach(p => {
        lookup[p.player_id] = p.rank;
      });
    }
    return lookup;
  }, [leaderboard]);

  // Fetch archived players
  const fetchArchivedPlayers = async () => {
    if (!ladder?.id) {
      console.warn('Cannot fetch archived players: ladder.id is undefined');
      return;
    }
    try {
      const token = localStorage.getItem('padel-ladder-token');
      const res = await axios.get(`${API}/api/ladder/${ladder.id}/players/archived`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArchivedPlayers(res.data);
    } catch (err) {
      console.error('Failed to fetch archived players:', err);
    }
  };

  // Toggle archived view
  const toggleArchived = async () => {
    if (!showArchived) {
      // Fetch archived when showing
      await fetchArchivedPlayers();
    }
    setShowArchived(!showArchived);
  };

  // Restore player
  const handleRestore = async (playerId) => {
    try {
      const token = localStorage.getItem('padel-ladder-token');
      await axios.put(`${API}/api/players/${playerId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('playerUpdated'));
      await fetchArchivedPlayers();
      refreshPlayers();
    } catch (err) {
      toast.error(t('error'));
    }
  };

  // Filter by search
  const currentPlayers = showArchived ? archivedPlayers : players;
  const filteredPlayers = currentPlayers.filter(p => 
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.display_name?.toLowerCase().includes(search.toLowerCase()) ||
    p.email?.toLowerCase().includes(search.toLowerCase())
  );

  // Sort players
  const sortedPlayers = useMemo(() => {
    return [...filteredPlayers].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'display_name':
          const aName = a.display_name || a.name;
          const bName = b.display_name || b.name;
          comparison = aName.localeCompare(bName);
          break;
        case 'gender':
          if (!a.gender && !b.gender) comparison = 0;
          else if (!a.gender) comparison = 1;
          else if (!b.gender) comparison = -1;
          else comparison = a.gender.localeCompare(b.gender);
          break;
        case 'rank':
          const aRank = rankLookup[a.id] || 999;
          const bRank = rankLookup[b.id] || 999;
          comparison = aRank - bRank;
          break;
        case 'mu':
          if (a.mu == null && b.mu == null) comparison = 0;
          else if (a.mu == null) comparison = 1;
          else if (b.mu == null) comparison = -1;
          else comparison = a.mu - b.mu;
          break;
        case 'sigma':
          if (a.sigma == null && b.sigma == null) comparison = 0;
          else if (a.sigma == null) comparison = 1;
          else if (b.sigma == null) comparison = -1;
          else comparison = a.sigma - b.sigma;
          break;
        case 'level':
          if (a.mu == null && b.mu == null) comparison = 0;
          else if (a.mu == null) comparison = 1;
          else if (b.mu == null) comparison = -1;
          else comparison = (a.mu / 5) - (b.mu / 5);
          break;
        case 'certainty':
          comparison = getCertaintySortValue(a.sigma) - getCertaintySortValue(b.sigma);
          break;
        case 'id':
        default:
          comparison = (a.created_at || '').localeCompare(b.created_at || '');
          break;
      }
      
      return sortDirection === 'desc' ? -comparison : comparison;
    });
  }, [filteredPlayers, sortField, sortDirection, rankLookup]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-50" />;
    return sortDirection === 'asc' 
      ? <ChevronUp className="h-3 w-3" /> 
      : <ChevronDown className="h-3 w-3" />;
  };

  const handleOpenDialog = (player = null) => {
    if (player) {
      setSelectedPlayer(player);
      setForm({ 
        name: player.name, 
        display_name: player.display_name || '',
        email: player.email || '', 
        phone: player.phone || '',
        gender: player.gender || '',
        mu: player.mu !== null && player.mu !== undefined ? String(player.mu) : '',
        sigma: player.sigma !== null && player.sigma !== undefined ? String(player.sigma) : '',
        titles: player.titles !== null && player.titles !== undefined ? String(player.titles) : '',
        runner_ups: player.runner_ups !== null && player.runner_ups !== undefined ? String(player.runner_ups) : ''
      });
    } else {
      setSelectedPlayer(null);
      setForm({ name: '', display_name: '', email: '', phone: '', gender: '', mu: '', sigma: '', titles: '', runner_ups: '' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    
    const playerData = {
      name: form.name,
      display_name: form.display_name || null,
      email: form.email || null,
      phone: form.phone || null,
      gender: form.gender || null,
      mu: form.mu ? parseFloat(form.mu) : null,
      sigma: form.sigma ? parseFloat(form.sigma) : null,
      titles: form.titles ? parseInt(form.titles, 10) : 0,
      runner_ups: form.runner_ups ? parseInt(form.runner_ups, 10) : 0
    };
    
    setLoading(true);
    try {
      if (selectedPlayer) {
        await updatePlayer(selectedPlayer.id, playerData);
        toast.success(t('playerUpdated'));
      } else {
        await addPlayer(ladder.id, playerData);
        toast.success(t('playerAdded'));
      }
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPlayer) return;
    
    setLoading(true);
    try {
      await deletePlayer(selectedPlayer.id);
      toast.success(t('playerDeleted'));
      setDeleteDialogOpen(false);
      setSelectedPlayer(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (player) => {
    setSelectedPlayer(player);
    setDeleteDialogOpen(true);
  };

  // Get certainty badge color
  const getCertaintyColor = (certaintyKey) => {
    switch (certaintyKey) {
      case 'certaintyVeryHigh': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'certaintyHigh': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'certaintyMedium': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'certaintyLow': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      case 'certaintyVeryLow': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  // Get certainty short label
  const getCertaintyLabel = (certaintyKey) => {
    if (!certaintyKey) return EMPTY;
    const labels = {
      certaintyVeryHigh: language === 'el' ? 'Π.Υψ' : 'VHi',
      certaintyHigh: language === 'el' ? 'Υψηλή' : 'High',
      certaintyMedium: language === 'el' ? 'Μέτρια' : 'Med',
      certaintyLow: language === 'el' ? 'Χαμηλή' : 'Low',
      certaintyVeryLow: language === 'el' ? 'Π.Χαμ' : 'VLo'
    };
    return labels[certaintyKey] || EMPTY;
  };

  return (
    <div className="p-4 lg:p-6" data-testid="admin-players-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="font-heading text-xl lg:text-2xl font-bold text-foreground mb-0.5">
            {showArchived ? t('archivedPlayers') : t('players')}
          </h1>
          <p className="text-sm text-muted-foreground">{currentPlayers.length} {t('totalPlayers').toLowerCase()}</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            size="sm"
            onClick={toggleArchived}
            className="gap-1.5 text-xs"
          >
            <Archive className="h-3.5 w-3.5" />
            {showArchived ? t('hideArchived') : t('showArchived')}
          </Button>
          {!showArchived && (
            <Button 
              onClick={() => handleOpenDialog()} 
              size="sm"
              className="gap-1.5 text-xs"
              data-testid="add-player-button"
            >
              <Plus className="h-3.5 w-3.5" />
              {t('addPlayer')}
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-9 text-sm"
          data-testid="player-search-input"
        />
      </div>

      {/* Players List */}
      {sortedPlayers.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border border-border" data-testid="no-players">
          <Users className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">{showArchived ? t('noPlayers') : t('noPlayers')}</p>
          {!showArchived && (
            <Button 
              onClick={() => handleOpenDialog()} 
              variant="outline" 
              size="sm"
              className="mt-3 gap-1.5 text-xs"
            >
              <Plus className="h-3.5 w-3.5" />
              {t('addPlayer')}
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden" data-testid="players-list">
          <div className="overflow-x-auto">
            <table className="text-sm" style={{ minWidth: 'max-content' }}>
              <thead className="bg-muted/60 border-b border-border">
                <tr>
                  {/* Display Name - LEFT */}
                  <th className={`px-3 py-2.5 text-left ${getHeaderClass(language)}`}>
                    {t('displayName')}
                  </th>
                  {/* Name - LEFT */}
                  <th className={`px-3 py-2.5 text-left ${getHeaderClass(language)}`}>
                    <button
                      onClick={() => handleSort('name')}
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                      data-testid="sort-by-name"
                    >
                      {t('name')}
                      <SortIcon field="name" />
                    </button>
                  </th>
                  {/* Gender - CENTER */}
                  <th className={`px-3 py-2.5 text-center ${getHeaderClass(language)}`}>
                    <button
                      onClick={() => handleSort('gender')}
                      className="flex items-center justify-center gap-1 hover:text-foreground transition-colors w-full"
                      data-testid="sort-by-gender"
                    >
                      {t('gender')}
                      <SortIcon field="gender" />
                    </button>
                  </th>
                  {/* Rank - CENTER */}
                  <th className={`px-3 py-2.5 text-center ${getHeaderClass(language)}`}>
                    <button
                      onClick={() => handleSort('rank')}
                      className="flex items-center justify-center gap-1 hover:text-foreground transition-colors w-full"
                      data-testid="sort-by-rank"
                    >
                      #
                      <SortIcon field="rank" />
                    </button>
                  </th>
                  {/* Level - CENTER */}
                  <th className={`px-3 py-2.5 text-center ${getHeaderClass(language)}`}>
                    <button
                      onClick={() => handleSort('level')}
                      className="flex items-center justify-center gap-1 hover:text-foreground transition-colors w-full"
                      data-testid="sort-by-level"
                    >
                      {t('level')}
                      <SortIcon field="level" />
                    </button>
                  </th>
                  {/* Certainty - CENTER */}
                  <th className={`px-3 py-2.5 text-center ${getHeaderClass(language)}`}>
                    <button
                      onClick={() => handleSort('certainty')}
                      className="flex items-center justify-center gap-1 hover:text-foreground transition-colors w-full"
                      data-testid="sort-by-certainty"
                    >
                      {t('certainty')}
                      <SortIcon field="certainty" />
                    </button>
                  </th>
                  {/* μ - CENTER */}
                  <th className={`px-3 py-2.5 text-center ${getHeaderClass(language)}`}>
                    <button
                      onClick={() => handleSort('mu')}
                      className="flex items-center justify-center gap-1 hover:text-foreground transition-colors w-full"
                      data-testid="sort-by-mu"
                    >
                      μ
                      <SortIcon field="mu" />
                    </button>
                  </th>
                  {/* σ - CENTER */}
                  <th className={`px-3 py-2.5 text-center ${getHeaderClass(language)}`}>
                    <button
                      onClick={() => handleSort('sigma')}
                      className="flex items-center justify-center gap-1 hover:text-foreground transition-colors w-full"
                      data-testid="sort-by-sigma"
                    >
                      σ
                      <SortIcon field="sigma" />
                    </button>
                  </th>
                  {/* Phone - RIGHT */}
                  <th className={`px-3 py-2.5 text-right ${getHeaderClass(language)}`}>
                    {t('phone')}
                  </th>
                  {/* Actions - RIGHT */}
                  <th className={`px-3 py-2.5 text-right ${getHeaderClass(language)}`}>
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedPlayers.map((player, index) => {
                  const certaintyKey = getCertaintyFromSigma(player.sigma);
                  const level = getLevelFromMu(player.mu);
                  const rank = rankLookup[player.id];
                  const isEven = index % 2 === 0;
                  
                  return (
                    <tr 
                      key={player.id} 
                      className={`border-b border-border/50 last:border-0 hover:bg-muted/40 transition-colors ${isEven ? 'bg-background' : 'bg-muted/20'}`}
                      data-testid={`player-row-${index}`}
                    >
                      {/* Display Name - LEFT */}
                      <td className="px-3 py-2 text-left font-medium text-foreground whitespace-nowrap">
                        {player.display_name || EMPTY}
                      </td>
                      {/* Name - LEFT */}
                      <td className="px-3 py-2 text-left text-muted-foreground whitespace-nowrap">
                        {player.name}
                      </td>
                      {/* Gender - CENTER */}
                      <td className="px-3 py-2 text-center text-muted-foreground whitespace-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {player.gender === 'male' ? 'M' : player.gender === 'female' ? 'F' : EMPTY}
                      </td>
                      {/* Rank - CENTER */}
                      <td className="px-3 py-2 text-center font-medium whitespace-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {rank ? `#${rank}` : EMPTY}
                      </td>
                      {/* Level - CENTER */}
                      <td className="px-3 py-2 text-center font-medium whitespace-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {level || EMPTY}
                      </td>
                      {/* Certainty - CENTER */}
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        {certaintyKey ? (
                          <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${getCertaintyColor(certaintyKey)}`}>
                            {getCertaintyLabel(certaintyKey)}
                          </span>
                        ) : EMPTY}
                      </td>
                      {/* μ - CENTER */}
                      <td className="px-3 py-2 text-center text-muted-foreground whitespace-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatNumber(player.mu) || EMPTY}
                      </td>
                      {/* σ - CENTER */}
                      <td className="px-3 py-2 text-center text-muted-foreground whitespace-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatNumber(player.sigma) || EMPTY}
                      </td>
                      {/* Phone - RIGHT */}
                      <td className="px-3 py-2 text-right text-muted-foreground text-xs whitespace-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {player.phone || EMPTY}
                      </td>
                      {/* Actions - RIGHT */}
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-0.5">
                          {showArchived ? (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                              onClick={() => handleRestore(player.id)}
                              data-testid={`restore-player-${index}`}
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </Button>
                          ) : (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-7 w-7 p-0"
                                onClick={() => handleOpenDialog(player)}
                                data-testid={`edit-player-${index}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                                onClick={() => openDeleteDialog(player)}
                                data-testid={`delete-player-${index}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="player-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedPlayer ? t('edit') + ' ' + t('player') : t('addPlayer')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="playerName" className="text-xs">{t('name')} *</Label>
                <Input
                  id="playerName"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Konstantinos Papadopoulos"
                  required
                  className="h-9"
                  data-testid="player-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="displayName" className="text-xs">{t('displayName')}</Label>
                <Input
                  id="displayName"
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  placeholder={language === 'el' ? 'π.χ. Κώστας' : 'e.g. Kostas'}
                  className="h-9"
                  data-testid="player-display-name-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="playerEmail" className="text-xs">{t('email')}</Label>
                <Input
                  id="playerEmail"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="email@example.com"
                  className="h-9"
                  data-testid="player-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="playerPhone" className="text-xs">{t('phone')}</Label>
                <Input
                  id="playerPhone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+30 123 456 7890"
                  className="h-9"
                  data-testid="player-phone-input"
                />
              </div>
            </div>
            
            {/* Gender */}
            <div className="space-y-1.5">
              <Label htmlFor="playerGender" className="text-xs">{t('gender')}</Label>
              <Select value={form.gender} onValueChange={(val) => setForm({ ...form, gender: val })}>
                <SelectTrigger className="h-9" data-testid="player-gender-select">
                  <SelectValue placeholder={language === 'el' ? 'Επιλέξτε...' : 'Select...'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">{t('male')} (M)</SelectItem>
                  <SelectItem value="female">{t('female')} (F)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Skill Rating */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="playerMu" className="text-xs">
                  μ ({t('skillMu').split('(')[1]?.replace(')', '') || 'Skill'})
                  <span className="text-[10px] text-muted-foreground ml-1">(0-50)</span>
                </Label>
                <Input
                  id="playerMu"
                  type="number"
                  step="0.01"
                  min="0"
                  max="50"
                  value={form.mu}
                  onChange={(e) => setForm({ ...form, mu: e.target.value })}
                  placeholder="25.00"
                  className="h-9"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                  data-testid="player-mu-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="playerSigma" className="text-xs">
                  σ ({t('certaintySigma').split('(')[1]?.replace(')', '') || 'Certainty'})
                  <span className="text-[10px] text-muted-foreground ml-1">(0-16)</span>
                </Label>
                <Input
                  id="playerSigma"
                  type="number"
                  step="0.01"
                  min="0"
                  max="16"
                  value={form.sigma}
                  onChange={(e) => setForm({ ...form, sigma: e.target.value })}
                  placeholder="8.33"
                  className="h-9"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                  data-testid="player-sigma-input"
                />
              </div>
            </div>
            
            {/* Achievements (Titles & Runner-Ups) */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="playerTitles" className="text-xs">
                  🏆 {t('titles')}
                </Label>
                <Input
                  id="playerTitles"
                  type="number"
                  min="0"
                  value={form.titles}
                  onChange={(e) => setForm({ ...form, titles: e.target.value })}
                  placeholder="0"
                  className="h-9"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                  data-testid="player-titles-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="playerRunnerUps" className="text-xs">
                  🥈 {t('runnerUps')}
                </Label>
                <Input
                  id="playerRunnerUps"
                  type="number"
                  min="0"
                  value={form.runner_ups}
                  onChange={(e) => setForm({ ...form, runner_ups: e.target.value })}
                  placeholder="0"
                  className="h-9"
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                  data-testid="player-runner-ups-input"
                />
              </div>
            </div>
            
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="submit" size="sm" disabled={loading} data-testid="save-player-button">
                {loading ? t('loading') : t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation - now Archive */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-player-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('archive')} {selectedPlayer?.display_name || selectedPlayer?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'el' 
                ? 'Ο παίκτης θα αρχειοθετηθεί και μπορεί να επαναφερθεί αργότερα.'
                : 'The player will be archived and can be restored later.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-delete-player"
            >
              {t('archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
